import { createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsFileEarmarkText } from 'react-icons/bs'
import { useSelector } from 'react-redux'

import {
  useGetOverdueReturnReceiptsQuery,
  useGetReturnReceiptRemindersQuery,
} from '../../api/services/filesApi'
import ReturnReceiptReminderModal from '../modals/files/ReturnReceiptReminderModal'

const ReturnReceiptReminderContext = createContext(null)

export const useReturnReceiptReminder = () => useContext(ReturnReceiptReminderContext)

export const ReturnReceiptReminderProvider = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [requireAcknowledge, setRequireAcknowledge] = useState(false)
  const user = useSelector((state) => state.user.user)
  const company = useSelector((state) => state.company.company)

  const { data: overdueReturnReceipts } = useGetOverdueReturnReceiptsQuery(
    { companyId: company?.id },
    {
      skip: !user?.id || !company?.id,
      pollingInterval: 15 * 60 * 1000,
    },
  )

  const { data: unacknowledgedReminders } = useGetReturnReceiptRemindersQuery(
    { companyId: company?.id },
    {
      skip: !user?.id || !company?.id,
      pollingInterval: 15 * 60 * 1000,
    },
  )

  const count = overdueReturnReceipts?.length ?? 0

  useEffect(() => {
    if (user?.token && unacknowledgedReminders?.length > 0) {
      setRequireAcknowledge(true)
      setOpen(true)
    }
  }, [unacknowledgedReminders, user?.token])

  const handleSetOpen = (isOpen) => {
    setOpen(isOpen)
    if (!isOpen) {
      setRequireAcknowledge(false)
    }
  }

  const openModal = () => {
    setRequireAcknowledge(false)
    setOpen(true)
  }

  return (
    <ReturnReceiptReminderContext.Provider value={{ count, openModal }}>
      {children}
      <ReturnReceiptReminderModal
        open={open}
        setOpen={handleSetOpen}
        data={overdueReturnReceipts}
        requireAcknowledge={requireAcknowledge}
      />
    </ReturnReceiptReminderContext.Provider>
  )
}

export const ReturnReceiptReminderIconButton = ({ light = false }) => {
  const { t } = useTranslation()
  const { count, openModal } = useReturnReceiptReminder() ?? {}

  if (!count) return null

  return (
    <button
      type="button"
      onClick={openModal}
      className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${
        light ? 'hover:bg-white/20' : 'hover:bg-sky-100'
      }`}
      title={t('return_receipt_overdue_title')}
      aria-label={t('return_receipt_overdue_title')}
    >
      <BsFileEarmarkText className={light ? 'text-white' : 'text-[#073C59]'} size={22} />
      <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[11px] font-semibold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
        {count}
      </span>
    </button>
  )
}
